import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useAppData } from "../../../context/AppDataContext";
import BurndownChart from "../../sprint/components/BurndownChart";
import TaskTable from "../../task/TaskTable";
import TaskKanban from "../../task/TaskKanban";
import TaskDetailDrawer from "../../task/TaskDetailDrawer";
import NovelEditor from "../../core/NovelEditor";
import Badge from "../../core/Badge";
import useDebounce from "../../../hooks/useDebounce";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuthInfo } from "../../../context/AuthContext";
import { errorToast, successfulToast } from "../../../util/toastNotifications";
import Loading from "../../layout/Loading";

import {
  faList,
  faColumns,
  faFlag,
  faEllipsisV,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { setCachedBreadcrumbLabel } from "../../../helpers/breadcrumbCache";

const SPRINT_TASKS_PER_PAGE = 100;

const fetchAllSprintTasks = async (api, sprintId) => {
  const baseParams = {
    include: ["assigned_users", "status"],
    sprint_id: sprintId,
    sort: "priority",
    sort_dir: "asc",
    per_page: SPRINT_TASKS_PER_PAGE,
  };

  const allTasks = [];
  let page = 1;

  while (true) {
    const batch = await api.task.getTasks({ ...baseParams, page });
    allTasks.push(...batch);
    if (batch.length < SPRINT_TASKS_PER_PAGE) {
      break;
    }
    page += 1;
  }

  return allTasks;
};

const getSprintStatus = (startDate, endDate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  if (end < today) return "past";
  if (start <= today && end >= today) return "current";
  return "upcoming";
};

const SprintDetailPage = () => {
  const { sprintId } = useParams();
  const history = useHistory();
  const { api } = useAppData();
  const [sprint, setSprint] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("list");
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isBurndownCollapsed, setIsBurndownCollapsed] = useState(false);
  const [isDescriptionCollapsed, setIsDescriptionCollapsed] = useState(false);
  const [isTasksCollapsed, setIsTasksCollapsed] = useState(false);
  const [chartMode, setChartMode] = useState("burndown");
  const [description, setDescription] = useState(null);
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [filterParameter, setFilterParameter] = useState(false);
  const [filteredTasks, setFilteredTasks] = useState(null);
  const originalDescriptionRef = useRef(null);
  const dropdownRef = useRef(null);
  const { userInfo } = useAuthInfo();
  const canManageSprintTasks =
    userInfo?.role?.name === "admin" || userInfo?.role?.name === "super-admin";
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [resetSelectionKey, setResetSelectionKey] = useState(0);
  const [isRemovingTasks, setIsRemovingTasks] = useState(false);

  useEffect(() => {
    const fetchSprintData = async () => {
      try {
        const needsFullPageLoader =
          !sprint || String(sprint.sprint_id) !== String(sprintId);
        if (needsFullPageLoader) {
          setLoading(true);
        }
        const [sprintData, tasksData, statusesData] = await Promise.all([
          api.sprint.getSprintById(sprintId),
          fetchAllSprintTasks(api, sprintId),
          api.taskStatus.getTaskStatuses(),
        ]);

        const sprintStatus = getSprintStatus(
          sprintData.start_date,
          sprintData.end_date
        );

        const updatedSprint = {
          ...sprintData,
          sprint_status: sprintStatus,
        };

        setSprint(updatedSprint);
        setTasks(tasksData);
        setTaskStatuses(statusesData);
        setProjects(sprintData.projects || []);

        if (updatedSprint.description) {
          try {
            const parsed = JSON.parse(updatedSprint.description);
            setDescription(parsed);
            originalDescriptionRef.current = updatedSprint.description;
          } catch {
            setDescription(null);
            originalDescriptionRef.current = null;
          }
        } else {
          setDescription(null);
          originalDescriptionRef.current = null;
        }
      } catch (error) {
        console.error("Error fetching sprint data:", error);
      } finally {
        setLoading(false);
      }
    };
    if (!isDrawerOpen) {
      fetchSprintData();
    }
  }, [api, sprintId, isDrawerOpen]);

  const tasksToDisplay = filteredTasks ?? tasks;

  useEffect(() => {
    if (!tasks?.length) return;

    if (filterParameter === "new") {
      setFilteredTasks(tasks.filter((task) => task.status === "New"));
    } else if (filterParameter === "current") {
      setFilteredTasks(
        tasks.filter(
          (task) =>
            task.status !== "New" &&
            task.status !== "Complete" &&
            task.status !== "Awaiting QA" &&
            task.status !== "QA"
        )
      );
    } else if (filterParameter === "qa") {
      setFilteredTasks(
        tasks.filter(
          (task) => task.status === "Awaiting QA" || task.status === "QA"
        )
      );
    } else if (filterParameter === "complete") {
      setFilteredTasks(tasks.filter((task) => task.status === "Complete"));
    } else {
      setFilteredTasks(tasks);
    }
  }, [tasks, filterParameter]);

  useEffect(() => {
    if (sprint?.sprint_id && sprint?.name) {
      setCachedBreadcrumbLabel("sprint", sprint.sprint_id, sprint.name);
    }
  }, [sprint]);

  const debouncedDescription = useDebounce(description, 1000);

  const saveDescription = async () => {
    const descriptionString = JSON.stringify(debouncedDescription);

    if (descriptionString === originalDescriptionRef.current) {
      return;
    }

    try {
      setIsSavingDescription(true);
      await api.sprint.updateSprint(sprintId, {
        description: descriptionString,
      });
      const updatedSprint = await api.sprint.getSprintById(sprintId);
      setSprint(updatedSprint);
      originalDescriptionRef.current = descriptionString;
    } catch (error) {
      console.error("Error updating sprint description:", error);
    } finally {
      setIsSavingDescription(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleTaskStatusChange = async (taskId, taskStatusId) => {
    const status = taskStatuses.find((s) => s.task_status_id === taskStatusId);

    if (!status) {
      console.error("Status not found:", taskStatusId);
      return;
    }

    const originalTasks = tasks;
    const originalTask = tasks.find((task) => task.task_id === taskId);

    setTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.task_id === taskId
          ? {
              ...task,
              status: status.name,
              status_color: status.color,
              status_id: status.task_status_id,
            }
          : task
      )
    );

    try {
      const response = await api.task.updateTask(taskId, {
        status_id: status.task_status_id,
        version: originalTask.version,
      });

      if (response) {
        setTasks((prevTasks) =>
          prevTasks.map((task) =>
            task.task_id === taskId
              ? {
                  ...task,
                  status: response.status,
                  status_color: response.status_color,
                  status_id: response.status_id,
                  version: response.version,
                }
              : task
          )
        );
      }
    } catch (error) {
      console.error("Error updating task status:", error);
      setTasks(originalTasks);
    }
  };

  const handleTaskClick = (taskId) => {
    setSelectedTaskId(taskId);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedTaskId(null);
  };

  const handleEditSprint = () => {
    setShowDropdown(false);
    history.push(`/sprint-form/${sprintId}`);
  };

  const handleStartRemoveFromSprint = () => {
    setShowDropdown(false);
    setIsSelectionMode(true);
  };

  const handleCancelSelection = () => {
    setIsSelectionMode(false);
    setSelectedTasks([]);
    setResetSelectionKey((prev) => prev + 1);
  };

  const handleRemoveFromSprint = async () => {
    if (selectedTasks.length === 0 || isRemovingTasks) return;

    try {
      setIsRemovingTasks(true);
      const result = await api.sprint.removeTasksFromSprint(
        sprintId,
        selectedTasks
      );
      const removedCount = result?.removed_count ?? selectedTasks.length;
      const removedTaskIds = new Set(selectedTasks);

      setTasks((prevTasks) =>
        prevTasks.filter((task) => !removedTaskIds.has(task.task_id))
      );
      handleCancelSelection();
      successfulToast(
        `Successfully removed ${removedCount} task${
          removedCount === 1 ? "" : "s"
        } from the sprint`
      );
    } catch (error) {
      console.error("Error removing tasks from sprint:", error);
      errorToast(error?.message || "Failed to remove tasks from sprint");
    } finally {
      setIsRemovingTasks(false);
    }
  };

  if (loading) {
    return (
      <div className="sprint-detail-loading">
        <Loading />
      </div>
    );
  }

  if (!sprint) {
    return <div>Sprint not found</div>;
  }

  const calculateSprintStats = () => {
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter((task) => {
      const isComplete =
        typeof task.status === "object"
          ? task.status?.is_complete || task.status?.is_closed
          : task.status?.name?.toLowerCase() === "complete" ||
            task.status?.toLowerCase() === "complete";
      return isComplete;
    }).length;
    const totalPoints = tasks.reduce(
      (sum, task) => sum + (task.points_estimate || 0),
      0
    );
    const completedPoints = tasks
      .filter((task) => {
        const isComplete =
          typeof task.status === "object"
            ? task.status?.is_complete || task.status?.is_closed
            : task.status?.name?.toLowerCase() === "complete" ||
              task.status?.toLowerCase() === "complete";
        return isComplete;
      })
      .reduce((sum, task) => sum + (task.points_estimate || 0), 0);
    const progress =
      totalPoints > 0 ? Math.round((completedPoints / totalPoints) * 100) : 0;

    const stats = {
      totalTasks,
      completedTasks,
      totalPoints,
      completedPoints,
      progress,
    };

    if (sprint?.sprint_id) {
      const sprintProgress = {
        sprint_id: sprint.sprint_id,
        progress: stats.progress,
        totalTasks: stats.totalTasks,
        completedTasks: stats.completedTasks,
        totalPoints: stats.totalPoints,
        completedPoints: stats.completedPoints,
        lastUpdated: Date.now(),
      };
      localStorage.setItem(
        `sprint_progress_${sprint.sprint_id}`,
        JSON.stringify(sprintProgress)
      );
    }

    return stats;
  };

  const stats = calculateSprintStats();

  return (
    <div className="width-full max-width-full sprint-detail-page">
      <div className="detail-header">
        <div className="header-main">
          <h1>
            <FontAwesomeIcon icon={faFlag} className="mr-2" />
            {sprint.name}
          </h1>
          {userInfo?.role?.name !== "user" && (
            <div className="action-buttons mt-4">
              <FontAwesomeIcon
                icon={faEllipsisV}
                className="dropdown-toggle"
                onClick={() => setShowDropdown(!showDropdown)}
              />
              {showDropdown && (
                <div className="dropdown-menu show position-right">
                  <button className="dropdown-item" onClick={handleEditSprint}>
                    Edit Sprint
                  </button>
                  {canManageSprintTasks && (
                    <button
                      className="dropdown-item"
                      onClick={handleStartRemoveFromSprint}
                    >
                      Remove from Sprint
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="expanded-info flex-rows mt-4 flex-wrap ">
          <div className="info-item pb-4">
            <span className="info-label">Created By</span>
            <span className="info-value">
              {sprint.created_by ? (
                <a href={`/user/${sprint.created_by.user_id}`}>
                  {sprint.created_by.first_name} {sprint.created_by.last_name}
                </a>
              ) : (
                "N/A"
              )}
            </span>
          </div>

          <div className="info-item">
            <span className="info-label">Total Tasks</span>
            <span className="info-value">{stats.totalTasks}</span>
          </div>

          <div className="info-item">
            <span className="info-label">Completed Tasks</span>
            <span className="info-value">{stats.completedTasks}</span>
          </div>

          <div className="info-item">
            <span className="info-label">Total Points</span>
            <span className="info-value">{stats.totalPoints}</span>
          </div>

          <div className="info-item">
            <span className="info-label">Completed Points</span>
            <span className="info-value">{stats.completedPoints}</span>
          </div>
        </div>

        <div className="header-info">
          <div className="info-item">
            <span className="info-label">Status</span>
            <span className="info-value">
              <Badge type="sprint_status" status={sprint.sprint_status} />
            </span>
          </div>

          {sprint.project && (
            <div className="info-item">
              <span className="info-label">Project</span>
              <span className="info-value">
                <a href={`/project/${sprint.project.project_id}`}>
                  {sprint.project.name}
                </a>
              </span>
            </div>
          )}

          <div className="info-item">
            <span className="info-label">Start Date</span>
            <span className="info-value">
              {new Date(sprint.start_date).toLocaleDateString()}
            </span>
          </div>

          <div className="info-item">
            <span className="info-label">End Date</span>
            <span className="info-value">
              {new Date(sprint.end_date).toLocaleDateString()}
            </span>
          </div>

          <div className="info-item">
            <span className="info-label">Progress</span>
            <span className="info-value">{stats.progress}%</span>
          </div>
        </div>
      </div>

      <div className="card width-full">
        <div className="card-header">
          <h3 className=" d-flex align-items-center">
            Description
            <span
              className="ml-2 chevron-toggle"
              onClick={() => setIsDescriptionCollapsed(!isDescriptionCollapsed)}
            >
              <FontAwesomeIcon
                icon={!isDescriptionCollapsed ? faChevronRight : faChevronDown}
              />
            </span>
          </h3>
          {isDescriptionCollapsed && (
            <div className="info-item width-full mt-8">
              <span className="info-label">
                Description
                {isSavingDescription && (
                  <span
                    style={{
                      marginLeft: "8px",
                      fontSize: "0.875rem",
                      color: "#666",
                    }}
                  >
                    Saving...
                  </span>
                )}
              </span>
              <div className="info-value">
                <NovelEditor
                  initialValue={description}
                  onChange={setDescription}
                  onBlur={saveDescription}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card width-full mb-4">
        <div className="card-header sprint-burndown-header">
          <h3 className="mb-0 d-flex align-items-center">
            Sprint Chart
            <span
              className="ml-2 chevron-toggle"
              onClick={() => setIsBurndownCollapsed(!isBurndownCollapsed)}
            >
              <FontAwesomeIcon
                icon={!isBurndownCollapsed ? faChevronRight : faChevronDown}
              />
            </span>
          </h3>
          {isBurndownCollapsed && (
            <div className="chart-mode-toggle">
              <button
                className={`tab-button mr-2 ${
                  chartMode === "burndown" ? "active" : ""
                }`}
                onClick={() => setChartMode("burndown")}
              >
                Burndown
              </button>
              <button
                className={`tab-button ${
                  chartMode === "burnup" ? "active" : ""
                }`}
                onClick={() => setChartMode("burnup")}
              >
                Burnup
              </button>
            </div>
          )}
        </div>
        {isBurndownCollapsed && (
          <div className="card-body">
            <BurndownChart
              sprint={sprint}
              tasks={tasks}
              chartMode={chartMode}
            />
          </div>
        )}
      </div>
      <div className="card">
        <span className="sprint-project-text">Projects</span>

        {loading ? (
          <p>Loading Projects..</p>
        ) : (
          <ul>
            {projects?.map((project) => (
              <li key={project.project_id}>{project.name}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="card page-container width-full sprint-tasks-card">
        <div className="left-column">
          <div className="project-tasks">
            <div className="tasks-header">
              <div className="button-row my-0 no-wrap">
                <h3 className="mb-0 sprint-tasks-header">
                  <span className="sprint-tasks-text">Sprint Tasks</span>
                  <span
                    className="ml-2 chevron-toggle"
                    onClick={() => setIsTasksCollapsed(!isTasksCollapsed)}
                  >
                    <FontAwesomeIcon
                      icon={isTasksCollapsed ? faChevronRight : faChevronDown}
                    />
                  </span>
                </h3>
              </div>
              {!isTasksCollapsed && (
                <div className="flex-rows">
                  <button
                    className={`tab-button ${
                      viewMode === "kanban" ? "active" : ""
                    }`}
                    onClick={() => {
                      if (isSelectionMode) {
                        handleCancelSelection();
                      }
                      setViewMode("kanban");
                    }}
                  >
                    <FontAwesomeIcon icon={faColumns} /> Kanban
                  </button>
                  <button
                    className={`tab-button mr-2 ${
                      viewMode === "list" ? "active" : ""
                    }`}
                    onClick={() => {
                      setViewMode("list");
                      setFilterParameter("all");
                    }}
                  >
                    <FontAwesomeIcon icon={faList} /> List
                  </button>
                  {!isTasksCollapsed && viewMode === "list" && (
                    <div className="flex-rows">
                      <button
                        className={`tab-button ${
                          filterParameter === "new" ? "selected" : ""
                        }`}
                        onClick={() => {
                          setFilterParameter("new");
                        }}
                      >
                        New
                      </button>

                      <button
                        className={`tab-button ${
                          filterParameter === "current" ? "selected" : ""
                        }`}
                        onClick={() => {
                          setFilterParameter("current");
                        }}
                      >
                        Current Tasks
                      </button>

                      <button
                        className={`tab-button ${
                          filterParameter === "qa" ? "selected" : ""
                        }`}
                        onClick={() => {
                          setFilterParameter("qa");
                        }}
                      >
                        QA
                      </button>

                      <button
                        className={`tab-button ${
                          filterParameter === "complete" ? "selected" : ""
                        }`}
                        onClick={() => {
                          setFilterParameter("complete");
                        }}
                      >
                        Complete
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {!isTasksCollapsed && (
              <>
                {viewMode === "list" &&
                  isSelectionMode &&
                  canManageSprintTasks && (
                    <div className="button-row sprint-tasks-action-bar my-4">
                      <div className="button-row-left">
                        <button
                          type="button"
                          className="cancel-button"
                          onClick={handleCancelSelection}
                        >
                          Cancel
                        </button>
                      </div>
                      <div className="button-row-right">
                        <button
                          type="button"
                          onClick={handleRemoveFromSprint}
                          disabled={
                            selectedTasks.length === 0 || isRemovingTasks
                          }
                        >
                          {isRemovingTasks
                            ? "Removing..."
                            : "Remove From Sprint"}
                        </button>
                      </div>
                    </div>
                  )}

                {viewMode === "list" ? (
                  <TaskTable
                    data={tasksToDisplay}
                    loading={loading}
                    onDataChange={setTasks}
                    onTaskClick={handleTaskClick}
                    showProjectColumn={true}
                    showToggleBtn={isSelectionMode && canManageSprintTasks}
                    onSelectionChange={setSelectedTasks}
                    resetSelectionKey={resetSelectionKey}
                  />
                ) : (
                  <div className="kanban-scroll-container scrollable-kanban">
                    <TaskKanban
                      tasks={tasks}
                      loading={loading}
                      onStatusChange={handleTaskStatusChange}
                      onTaskClick={handleTaskClick}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <TaskDetailDrawer
        taskId={selectedTaskId}
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        breadcrumbParent={{
          parentType: "sprint",
          parentId: sprintId,
          parentLabel: sprint?.name,
        }}
      />
    </div>
  );
};

export default SprintDetailPage;
